import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { IoCarSportOutline } from "react-icons/io5";
import {
  FiHome,
  FiTruck,
  FiUsers,
  FiCalendar,
  FiGrid,
  FiCreditCard,
  FiBarChart2,
  FiFileText,
  FiMapPin,
  FiMenu,
  FiX,
  FiSun,
  FiMoon,
  FiLogOut,
  FiSettings,
  FiShield,
  FiUserX,
  FiAlertTriangle,
  FiInfo,
  FiUser,
  FiBriefcase,
  FiInbox,
} from 'react-icons/fi';


const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const settingsDropdownRef = useRef(null);
  const { user, logout } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (settingsDropdownRef.current && !settingsDropdownRef.current.contains(event.target)) {
        setSettingsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Tableau de Bord', href: '/', icon: FiHome },
    { name: 'Voitures', href: '/cars', icon: IoCarSportOutline },
    { name: 'Clients', href: '/clients', icon: FiUsers },
    { name: 'Réservations', href: '/reservations', icon: FiCalendar },
    { name: 'Calendrier', href: '/calendar', icon: FiGrid },
    { name: 'Assurances', href: '/insurance', icon: FiShield },
    { name: 'Paiements', href: '/payments', icon: FiCreditCard },
    { name: 'Rapports', href: '/reports', icon: FiBarChart2 },

    { name: 'Documents', href: '/documents', icon: FiFileText },
    { name: 'Liste Noire', href: '/blacklist', icon: FiUserX },
    { name: 'Suivi GPS', href: '/gps-tracking', icon: FiMapPin },
    { name: 'Photos Avant', href: '/damage-reports', icon: FiAlertTriangle },

  ];

  // Add Users page for super admin only
  const adminNavigation = [
    { name: 'Utilisateurs', href: '/users', icon: FiShield },
  ];

  // SuperAdmin only sees Dashboard, Users, and Documents
  const superAdminNavigation = [
    { name: 'Tableau de Bord', href: '/', icon: FiHome },
    { name: 'Utilisateurs', href: '/users', icon: FiShield },
    { name: 'Demandes Agences', href: '/agency-update-requests', icon: FiInbox },
    { name: 'Documents', href: '/documents', icon: FiFileText },
    { name: 'Contrôle d\'Accès Liste Noire', href: '/global-blacklist-access', icon: FiShield },
    { name: 'Contrôle d\'Accès GPS', href: '/gps-access-control', icon: FiMapPin },
    { name: 'Sauvegarde & Restauration', href: '/backup', icon: FiSettings },
  ];

  // Determine navigation based on user role
  let allNavigation;
  if (user?.role === 'superadmin') {
    allNavigation = superAdminNavigation;
  } else if (user?.role === 'admin') {
    allNavigation = [...navigation, ...adminNavigation];
  } else {
    allNavigation = navigation;
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 shadow-lg transform transition-transform duration-300 ease-in-out flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 lg:static lg:inset-0`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex-1 flex justify-center items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="102" height="53" fill="none" viewBox="0 0 102 53">
              <path fill="#7662AB" stroke="#7662AB" stroke-miterlimit="10" d="M3.334 51.588.56 51.45l1.04-8.738 9.917-1.735a27.083 27.083 0 0 1 4.16-9.708 10899.55 10899.55 0 0 1-6.034-8.599l6.38-7.975 12.068 11.858a34.76 34.76 0 0 1 13.8-7.836l.762 2.495c-3.055.852-9.016 2.987-14.077 8.392a31.638 31.638 0 0 0-5.827 9.016 32.117 32.117 0 0 0-2.148 7.282l-3.053-1.173a39.312 39.312 0 0 1 8.53-16.018l-9.705-9.854-3.121 3.745 5.686 8.182a27.57 27.57 0 0 0-5.06 12.552L3.962 45.14c-.211 2.146-.42 4.296-.628 6.449Zm25.035 0a23.376 23.376 0 0 1 4.645-13.591c1.814-2.418 5.856-6.764 12.552-8.393 1.275-.308 8.792-2.01 16.227 2.36a22.839 22.839 0 0 1 8.184 8.598 23.323 23.323 0 0 1 2.842 11.096l2.706-.208c0-2.57-.38-8.37-4.162-14.077-4.341-6.553-12.248-11.584-21.496-11.305-10.069.304-17.883 6.789-21.498 13.938a25.128 25.128 0 0 0-2.636 11.444l2.636.138Z" />
              <path fill="#7662AB" stroke="#7662AB" stroke-miterlimit="10" d="M83.287 45.693a31.379 31.379 0 0 0-8.322-17.198l11.65-11.65-7.697-7.004-8.456 6.308a22.855 22.855 0 0 0-9.708-4.23l-1.456-7.975-2.427.637c.415 3.097.83 6.197 1.248 9.3a32.034 32.034 0 0 1 12.135 5.48l8.737-6.034 3.745 3.466-9.43 9.692a33.88 33.88 0 0 0-21.218-8.669L52.017.548l-9.847.907-1.815 10.47a32.312 32.312 0 0 0-5.192 1.729 31.842 31.842 0 0 0-4.577 2.427l-6.449-4.015-1.526 2.012 7.628 5.063a37.894 37.894 0 0 1 12.344-4.854l1.735-10.334 4.718-.422.206 16.712c4.827-.153 9.624.82 14.009 2.845C71.61 26.97 75.782 33.54 77.26 36.192a31.434 31.434 0 0 1 3.403 9.361l2.624.139Z" />
              <path fill="#7662AB" stroke="#7662AB" stroke-miterlimit="10" d="m100.56 51.45-.626-8.738-10.332-1.665a22.456 22.456 0 0 0-4.022-9.846l3.954-6.66-2.495-1.456-4.786 7.905a27.941 27.941 0 0 1 5.063 12.69l9.98 1.589.485 6.172 2.779.009Zm-42.893-5.814-2.703 2.237-8.27.208-3.176-2.288 2.808 2.86 8.792-.157 2.55-2.86Z" />
              <path fill="#7662AB" stroke="#7662AB" stroke-miterlimit="10" d="m39.308 45.988-2.747-2.652 28.19.209-2.55 2.495 4.16-2.289a17.24 17.24 0 0 0-6.664-7.54 45.769 45.769 0 0 0-19.348.311l-5.2 6.97 4.16 2.496Zm-.571 4.577c-.198-.993-2.058-.871-3.797-2.443a3.923 3.923 0 0 1-1.041-1.302c-.565-1.22-.227-2.472-.053-3.017a3.175 3.175 0 0 0-1.665.729 3.104 3.104 0 0 0-.83 1.145l-.052 3.797c.414.553.83 1.108 1.248 1.663l5.981.26c.176-.239.25-.538.209-.832Zm23.769.624 6.24-.102 1.197-1.302-.052-4.212-2.237-1.509c.134.436.488 1.79-.208 3.017-.454.796-1.134 1.18-1.924 1.613-.857.472-1.79.79-2.758.936l-.258 1.559Z" />
              <path fill="#fff" stroke="#fff" stroke-miterlimit="10" d="m64.168 42.66-12.326-.2.937-.623 4.627.052a1.413 1.413 0 0 1-.414-.833c-.091-.934.99-2.027 2.755-2.547l-.104-1.25a9.39 9.39 0 0 1 4.525 5.41v-.009Z" />
              <path fill="#7662AB" stroke="#7662AB" stroke-miterlimit="10" d="m35.615 41.984-.052-1.717c-.791-.583-1.796-.606-2.445-.104a1.914 1.914 0 0 0-.68 1.302l1.873.728 1.304-.209Z" />
              <path fill="#7662AB" stroke="#fff" stroke-miterlimit="10" d="M66.328 43.633a3.912 3.912 0 0 0 1.769-.31c.408-.18 1.238-.54 1.247-1.042.012-.569-1.043-1.15-1.975-1.134a2.391 2.391 0 0 0-1.146.311l.105 2.175Z" />
              <circle cx="22.725" cy="12.52" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
              <circle cx="18.642" cy="45.863" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
              <circle cx="42.686" cy="19.778" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
              <circle cx="58.11" cy="3.674" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
              <circle cx="88.958" cy="23.18" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
              <circle cx="82.153" cy="45.636" r="2" fill="#D9D9D9" stroke="#11A64A" stroke-width=".536" />
            </svg>


          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-6 scrollbar-thin">
          <div className="space-y-1">
            {allNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(item.href);
                    setSidebarOpen(false);
                  }}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </a>
              );
            })}
          </div>
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {user?.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white relative dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FiMenu className="h-6 w-6" />
            </button>

            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {darkMode ? <FiSun className="h-5 w-5" /> : <FiMoon className="h-5 w-5" />}
              </button>

              <div className="relative" ref={settingsDropdownRef}>
                <button
                  onClick={() => setSettingsDropdownOpen(!settingsDropdownOpen)}
                  className="flex items-center space-x-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FiSettings className={`h-5 w-5 transition-transform duration-200 ${settingsDropdownOpen ? 'rotate-90' : ''}`} />
                </button>

                {/* Settings Dropdown */}
                {settingsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 py-2 z-[100]">
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Paramètres
                      </p>
                    </div>

                    <button
                      onClick={() => {
                        navigate('/agency-info');
                        setSettingsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                    >
                      <FiBriefcase className="w-4 h-4 text-primary-500 flex-shrink-0" />
                      <span>Info Agence</span>
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FiLogOut className="h-5 w-5" />
                <span className="hidden sm:block">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout; 